import { Reveal, Kicker } from "./Reveal";
import { useLenis } from "lenis/react";
import CostCalculator from "./CostCalculator";

const STEPS = [
  ["1", "Recommendation generated", "PriceStrike generates a pricing recommendation.", "€0.005 · Recommendation Transaction"],
  ["2", "You review it", "You are free to use, compare or ignore it.", "No additional charge"],
  ["3", "You accept and apply it", "You implement the recommended price.", "A CDT is created"],
  ["4", "We invoice the CDT fee", "You pay the applicable value-based fee.", "Pay for the applied decision"],
];

const TIERS = [
  ["€0 – €99.99", "€0.09", "0.025% of value", "€0.09 + 0.025% of applied value"],
  ["€100 – €999.99", "€0.19", "0.025% of value", "€0.19 + 0.025% of applied value"],
  ["€1,000 and above", "€0.29", "0.025% of value", "€0.29 + 0.025% of applied value"],
];

const WHY = [
  ["01", "Aligned with value", "You pay more only when the applied decision has higher commercial importance."],
  ["02", "Encourages adoption", "A very low recommendation cost makes exploration and testing easier."],
  ["03", "Fair and transparent", "Clear rules, observable events and auditable billing logic."],
  ["04", "Scales with you", "Designed for low-ticket, high-volume as well as high-value commercial decisions."],
];

export default function Pricing() {
  const lenis = useLenis();
  return (
    <section id="pricing" className="border-b border-white/10" data-testid="pricing-section">
      <div className="mx-auto max-w-[1600px] px-6 py-28 lg:px-12 lg:py-40">
        <Reveal>
          <Kicker index="07" label="The business model" />
          <h2 className="max-w-5xl font-display text-4xl font-extrabold leading-[1.02] tracking-tighter sm:text-5xl lg:text-6xl" data-testid="pricing-heading">
            We monetise governed decision flow, <span className="text-[#00FF41]">not seats.</span>
          </h2>
          <p className="mt-8 max-w-2xl font-mono text-sm leading-relaxed text-zinc-400">
            PriceStrike has two primary transaction events. You decide. We charge for transactions.
          </p>
          <div className="mt-8 inline-flex flex-wrap gap-x-6 gap-y-2 border border-[#00FF41]/40 px-6 py-4 font-mono text-xs uppercase tracking-[0.25em] text-[#00FF41]" data-testid="pricing-no-subscription">
            <span>No subscriptions</span>
            <span className="text-zinc-600">·</span>
            <span>No seat pricing</span>
            <span className="text-zinc-600">·</span>
            <span>No minimums</span>
          </div>
        </Reveal>

        <div className="mt-20 grid gap-px border border-white/10 bg-white/10 md:grid-cols-2">
          <Reveal className="bg-[#050505] p-10">
            <h3 className="font-display text-2xl font-bold tracking-tight" data-testid="pricing-summary-charge">Recommendation Transaction</h3>
            <p className="mt-4 font-mono text-xs leading-relaxed text-zinc-500">
              A PriceStrike pricing recommendation is generated.{" "}
              <span className="text-white">€0.005 per recommendation</span> — inexpensive to explore.
            </p>
          </Reveal>
          <Reveal delay={0.1} className="bg-[#050505] p-10">
            <h3 className="font-display text-2xl font-bold tracking-tight" data-testid="pricing-summary-cdt">Commercial Decision Transaction</h3>
            <p className="mt-4 font-mono text-xs leading-relaxed text-zinc-500">
              A recommendation is <span className="text-[#00FF41]">accepted and applied</span> by the
              customer or its system. The fee combines a fixed charge based on the value of the applied
              decision, plus 0.025% of that value.
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <h3 className="mt-24 font-display text-3xl font-extrabold tracking-tighter" data-testid="pricing-how-heading">
            Recommendation → review → acceptance → <span className="text-zinc-600">value-based charge.</span>
          </h3>
        </Reveal>
        <div className="mt-12 grid border border-white/10 md:grid-cols-2 lg:grid-cols-4">
          {STEPS.map(([n, title, desc, charge], i) => (
            <Reveal key={n} delay={i * 0.08} className="border-b border-white/10 p-8 transition-colors duration-500 last:border-b-0 hover:bg-[#0F0F11] md:border-r md:last:border-r-0 lg:border-b-0">
              <div className="font-mono text-xs text-[#00FF41]">{n}</div>
              <div className="mt-8 font-display text-lg font-bold tracking-tight" data-testid={`pricing-step-${n}`}>{title}</div>
              <p className="mt-3 font-mono text-xs leading-relaxed text-zinc-500">{desc}</p>
              <div className="mt-6 border-t border-white/10 pt-4 font-mono text-[11px] uppercase tracking-[0.15em] text-[#00FF41]">{charge}</div>
            </Reveal>
          ))}
        </div>

        <div className="mt-24 grid items-stretch gap-6 lg:grid-cols-[1fr_auto_1fr]">
          <Reveal className="border border-white/10 bg-[#0F0F11] p-10">
            <div className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-500">1 · Recommendation Transaction (RT)</div>
            <div className="mt-8 flex items-baseline gap-3" data-testid="pricing-rt-box">
              <span className="font-display text-5xl font-extrabold tracking-tighter text-white">€0.005</span>
              <span className="font-mono text-xs text-zinc-500">per recommendation</span>
            </div>
            <p className="mt-4 font-mono text-xs leading-relaxed text-zinc-500">Every pricing recommendation generated by PriceStrike.</p>
          </Reveal>
          <Reveal delay={0.08} className="flex items-center justify-center font-display text-4xl font-extrabold text-[#00FF41]">+</Reveal>
          <Reveal delay={0.16} className="border border-[#00FF41]/40 bg-[#0F0F11] p-10">
            <div className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-500">2 · Commercial Decision Transaction (CDT)</div>
            <div className="mt-8 flex items-baseline gap-3" data-testid="pricing-cdt-box">
              <span className="font-display text-5xl font-extrabold tracking-tighter text-[#00FF41]">Tiered</span>
              <span className="font-mono text-xs text-zinc-500">value-based fee</span>
            </div>
            <p className="mt-4 font-mono text-xs leading-relaxed text-zinc-500">Charged only when a recommendation is accepted and applied.</p>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <h3 className="mt-24 font-display text-3xl font-extrabold tracking-tighter" data-testid="pricing-tiers-heading">
            CDT value tiers — <span className="text-zinc-600">the price of an applied decision scales with its commercial value.</span>
          </h3>
          <div className="mt-10 overflow-x-auto border border-white/10" data-testid="cdt-tier-table">
            <table className="w-full min-w-[720px] text-left font-mono text-xs">
              <thead>
                <tr className="border-b border-white/10 text-[11px] uppercase tracking-[0.2em] text-zinc-500">
                  {["Value of applied decision", "Fixed fee", "Variable fee", "Total CDT fee"].map((h) => (
                    <th key={h} className="px-6 py-4 font-medium">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {TIERS.map((row, i) => (
                  <tr key={row[0]} className={`border-b border-white/5 transition-colors duration-300 last:border-b-0 hover:bg-[#0F0F11] ${i === 2 ? "bg-[#0A0A0B]" : ""}`} data-testid={`cdt-tier-${i}`}>
                    <td className="px-6 py-5 text-white">{row[0]}</td>
                    <td className="px-6 py-5 text-[#00FF41]">{row[1]}</td>
                    <td className="px-6 py-5 text-zinc-400">{row[2]}</td>
                    <td className="px-6 py-5 text-zinc-300">{row[3]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-6 max-w-3xl font-mono text-xs leading-relaxed text-zinc-500">
            <span className="text-white">Commercial principle:</span> recommendation transactions make
            PriceStrike inexpensive to explore; Commercial Decision Transactions align revenue with
            decisions that are actually accepted and applied.
          </p>
          <div className="mt-8 border border-[#00FF41]/30 bg-[#0A0A0B] px-8 py-5 font-mono text-xs uppercase tracking-[0.2em] text-zinc-400" data-testid="pricing-trust-loop">
            Trust → Autonomy → More applied decisions → More transaction revenue →{" "}
            <span className="text-[#00FF41]">Deeper Decision Ledger</span>
          </div>
          <p className="mt-4 max-w-3xl font-mono text-xs leading-relaxed text-zinc-500">
            No seat pricing. No requirement to monetise unused software access. Revenue increases when
            customers actually use governed commercial decisions.
          </p>
        </Reveal>

        <CostCalculator />

        <div className="mt-24 grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-4">
          {WHY.map(([n, title, desc], i) => (
            <Reveal key={n} delay={i * 0.08} className="bg-[#050505] p-8 transition-colors duration-500 hover:bg-[#0F0F11]">
              <div className="font-mono text-xs text-[#00FF41]">{n}</div>
              <div className="mt-6 font-display text-lg font-bold tracking-tight" data-testid={`why-model-${n}`}>{title}</div>
              <p className="mt-3 font-mono text-xs leading-relaxed text-zinc-500">{desc}</p>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.15}>
          <div className="mt-24 flex flex-col items-start justify-between gap-8 border border-[#00FF41]/40 bg-[#0A0A0B] p-10 lg:flex-row lg:items-center lg:p-14" data-testid="pricing-cta-strip">
            <div>
              <h3 className="font-display text-2xl font-extrabold tracking-tighter sm:text-3xl">
                Explore with confidence. Decide with impact. <span className="text-[#00FF41]">Pay for results.</span>
              </h3>
              <p className="mt-4 max-w-xl font-mono text-xs leading-relaxed text-zinc-500">
                PriceStrike provides the data, models and governed decision infrastructure. You retain commercial authority.
              </p>
            </div>
            <button
              data-testid="pricing-cta-discuss"
              onClick={() => lenis?.scrollTo("#pilot", { offset: -72, duration: 1.6 })}
              className="shrink-0 bg-[#00FF41] px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-black transition-colors duration-300 hover:bg-white"
            >
              Discuss a design-partner pilot →
            </button>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
